import { useCallback, useEffect, useState } from 'react'
import { Plus } from 'lucide-react'
import { cachedGet } from '../lib/api.js'
import { buildDocInfo } from '../lib/doctorFilter.js'
import BookingCalendar from '../components/BookingCalendar.jsx'
import HuddlePanel from '../components/huddle/HuddlePanel.jsx'
import { Page, PageHeader, Button } from '../kit'
import { todayBKK } from '../lib/huddle.js'
import {
  ApptEditDrawer, buildLookup, buildTypeLookup, EMPTY_LOOKUP, asList,
  dayKey, bkkDayFromMs, bkkToMs,
} from './patient/PatientAppointments.jsx'

/**
 * Clinic-wide appointment scheduling (main module `/appointments`).
 *
 * Reuses the patient page's BookingCalendar + edit modal, but with no single
 * patient in scope: the หมอเดิม (patient's previous doctors) filter is dropped,
 * and there is no per-patient ประวัติ tab. Editing existing appointments works;
 * creating/booking a slot is still a stub pending the booking logic.
 */
export default function Appointments() {
  const [branch, setBranch] = useState(EMPTY_LOOKUP)
  // Dropdown lists for the edit modal — lazy-loaded on first edit/new.
  const [editData, setEditData] = useState({ type: EMPTY_LOOKUP, docInfo: {}, loaded: false, loading: false })
  const [editing, setEditing] = useState(null)
  const [creating, setCreating] = useState(false)
  const [pickedSlot, setPickedSlot] = useState(null) // {date, time, treatId, doctorId, branchSel, dur}
  const [calRefresh, setCalRefresh] = useState(0)    // bump → calendar re-fetches in place
  // Branch + day the calendar is showing, so the huddle strip above it stays in
  // scope. Month view has no meaningful day → today's huddle.
  const [scope, setScope] = useState({ branchId: '', day: todayBKK() })
  const onScopeChange = useCallback(({ branchId, day, calView }) => {
    setScope((prev) => {
      const next = { branchId, day: calView === 'day' ? day : todayBKK() }
      return prev.branchId === next.branchId && prev.day === next.day ? prev : next
    })
  }, [])

  // Branch lookup (for the modal's สาขา dropdown / branch names).
  useEffect(() => {
    let alive = true
    cachedGet('/api/branches').then((br) => {
      if (alive) setBranch(buildLookup({ status: 'fulfilled', value: br }, (b) => b.name || b.branch_name))
    }).catch(() => {})
    return () => { alive = false }
  }, [])

  // Lazy-load doctors + appointment types once, for the edit / new modal.
  const ensureEditData = useCallback(() => {
    setEditData((prev) => {
      if (prev.loaded || prev.loading) return prev
      Promise.allSettled([cachedGet('/api/doctors'), cachedGet('/api/appointment-types')]).then(([dr, ty]) => {
        setEditData({
          docInfo: buildDocInfo(dr.status === 'fulfilled' ? asList(dr.value) : []),
          type: buildTypeLookup(ty),
          loaded: true, loading: false,
        })
      })
      return { ...prev, loading: true }
    })
  }, [])
  const openEdit = useCallback((a) => { ensureEditData(); setEditing(a) }, [ensureEditData])
  const openCreate = useCallback(() => { ensureEditData(); setCreating(true) }, [ensureEditData])

  // Blank appointment for the create modal — no patient in scope on this page.
  const blankAppt = (() => {
    let start_date = null, date = dayKey(bkkDayFromMs(Date.now()))
    if (pickedSlot) {
      const [y, mo, d] = pickedSlot.date.split('-').map(Number)
      const [hh, mm] = pickedSlot.time.split(':').map(Number)
      start_date = bkkToMs(y, mo, d, hh, mm)
      date = pickedSlot.date
    }
    const branch_id = pickedSlot?.branchSel ? Number(pickedSlot.branchSel) : null
    return {
      patient_id: null, status: '',
      appointmentlist_id: pickedSlot?.treatId ? Number(pickedSlot.treatId) : null,
      doctor_id: pickedSlot?.doctorId ? Number(pickedSlot.doctorId) : null,
      branch_id, date, start_date, end_date: null,
      duration: Number(pickedSlot?.dur) || 30, details: '', remark: '', active: true,
    }
  })()

  return (
    <Page width="wide">
      {/* Title = the sidebar label (นัดหมาย). One primary action, top-right. */}
      <PageHeader
        title="นัดหมาย"
        subtitle="ตารางนัดหมายทุกสาขา · คลิกที่นัดหมายเพื่อแก้ไข"
        actions={<Button icon={Plus} onClick={openCreate}>นัดหมายใหม่</Button>}
      />

      <HuddlePanel branchId={scope.branchId} date={scope.day} />

      <BookingCalendar
        allowPrevDoctors={false}
        onScopeChange={onScopeChange}
        persistKey="appointments"
        liveUpdates
        refreshKey={calRefresh}
        onPickSlot={(date, time, treatId, doctorId, branchSel, dur) => { ensureEditData(); setPickedSlot({ date, time, treatId, doctorId, branchSel, dur }); setCreating(true) }}
        onEditAppt={(raw) => openEdit(raw)}
      />

      {editing && (
        <ApptEditDrawer
          mode="edit" a={editing} branch={branch} type={editData.type} docInfo={editData.docInfo}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); setCalRefresh((k) => k + 1) }}
          // No per-patient treatment index on this page, so `existingCount`
          // stays 0 and the duplicate check is the server's created:false answer.
          onTreatmentCreated={(res) => {
            setEditing(null)
            setCalRefresh((k) => k + 1)
            // The drawer itself toasts "สร้างการรักษาเรียบร้อยแล้ว · เปิด chart" (kit, 2026-10-18).
          }}
        />
      )}
      {creating && (
        <ApptEditDrawer
          mode="create" a={blankAppt} branch={branch} type={editData.type} docInfo={editData.docInfo}
          onClose={() => { setCreating(false); setPickedSlot(null) }}
          onCreate={() => { setCreating(false); setPickedSlot(null); setCalRefresh((k) => k + 1) /* the drawer toasts the success */ }}
        />
      )}
    </Page>
  )
}
